import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils';
import {
    SHIFT_CANCEL_SHORTCUT,
    SHIFT_CONFIRM_SHORTCUT,
    renderWithKeyShortcuts,
    ShortcutHint,
} from '@/components/shortcuts';
import type { ShiftFlowValue } from '../useShiftFlow';
import { SHIFT_STEP_ACTIONS } from '../shiftMeta';

const renderCancelButton = renderWithKeyShortcuts(
    'button',
    SHIFT_CANCEL_SHORTCUT.combination,
);
const renderConfirmButton = renderWithKeyShortcuts(
    'button',
    SHIFT_CONFIRM_SHORTCUT.combination,
);

/**
 * The shift dialog's footer (render-only). The primary button shows the
 * step's action (`useShiftFlow`'s `stepAction`) and runs it through
 * `confirmStep`, the same call `Enter` makes (SEL-49): Confirm count (pending
 * while the fresh expected cash loads) → Start/Close on a match or Get admin
 * approval on a discrepancy → on the co-sign, Start/Close once approved →
 * Done after a close. Beside it, Cancel dismisses the count step and Back
 * returns from the co-sign to the count. The row wraps when the labels don't
 * fit, like the payment and discount footers: Cancel and "Confirm count"
 * need more than the dialog's 335px even in English.
 */
export const ShiftModalFooter: React.FC<ShiftFlowValue> = ({
    step,
    stepAction,
    canConfirmStep,
    isConfirmingStep,
    confirmStep,
    closeModal,
    backToCount,
}) => {
    const { t } = useTranslation('pages');
    const primary = SHIFT_STEP_ACTIONS[stepAction];

    return (
        <Modal.Footer className="flex-wrap">
            {step === 'approve' ? (
                <Button
                    {...getNodeTestIdProp('seller-shift-back')}
                    onPress={backToCount}
                    variant="outline"
                >
                    {t('seller.shift.back')}
                </Button>
            ) : step === 'count' ? (
                <Button
                    {...getNodeTestIdProp('seller-shift-cancel')}
                    onPress={closeModal}
                    render={renderCancelButton}
                    variant="outline"
                >
                    {t('seller.shift.cancel')}
                    <ShortcutHint
                        placement="control"
                        shortcut={SHIFT_CANCEL_SHORTCUT}
                    />
                </Button>
            ) : null}
            <Button
                {...getNodeTestIdProp(primary.testId)}
                isDisabled={!canConfirmStep}
                isPending={isConfirmingStep}
                onPress={confirmStep}
                render={renderConfirmButton}
                variant="primary"
            >
                {t(primary.labelKey)}
                <ShortcutHint
                    placement="control"
                    shortcut={SHIFT_CONFIRM_SHORTCUT}
                />
            </Button>
        </Modal.Footer>
    );
};
